import React, { useState, useEffect, useCallback } from "react";
import { exportClientTransactionsPDF } from "../components/ClientTransactions/ExportPDF";
import ClientSummary from "../components/ClientTransactions/ClientSummary";
import AddTransactionForm from "../components/ClientTransactions/AddTransactionForm";
import TransactionsList from "../components/ClientTransactions/TransactionsList";
import { useParams, Link } from "react-router-dom";
import { Client, ClientTransaction } from "../types";
import { clientsAPI, clientTransactionsAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";

const ClientTransactions: React.FC = () => {
  // PDF Export Handler
  const handleExportPDF = () => {
    if (!client) return;
    exportClientTransactionsPDF({ client, transactions });
  };
  const { clientId } = useParams<{ clientId: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [transactions, setTransactions] = useState<ClientTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    type: "given" as "given" | "taken",
    amount: "",
    description: "",
    paymentMethod: "cash" as
      | "cash"
      | "bank_transfer"
      | "cheque"
      | "upi"
      | "card"
      | "other",
    category: "loan" as
      | "loan"
      | "payment"
      | "advance"
      | "refund"
      | "investment"
      | "other",
    notes: "",
    date: new Date().toISOString().split("T")[0],
  });

  const fetchData = useCallback(async () => {
    if (!clientId) return;

    try {
      if (initialLoad) {
        setLoading(true);
      }
      const [clientData, transactionsResponse] = await Promise.all([
        clientsAPI.getClient(clientId),
        clientTransactionsAPI.getClientTransactionsByClient(clientId),
      ]);
      setClient(clientData);
      setTransactions(transactionsResponse.transactions);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [clientId, initialLoad]);

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return;

    // Validate required fields
    if (!formData.amount || !formData.description.trim()) {
      setError("Amount and description are required");
      return;
    }
    
    // Validate amount
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0 || !isFinite(amount)) {
      setError("Please enter a valid positive amount");
      return;
    }

    try {
      setError(""); // Clear any previous errors
      await clientTransactionsAPI.createClientTransaction({
        client: clientId,
        type: formData.type,
        amount: amount,
        description: formData.description.trim(),
        paymentMethod: formData.paymentMethod,
        category: formData.category,
        notes: formData.notes.trim(),
        date: formData.date,
      });

      setFormData({
        type: "given",
        amount: "",
        description: "",
        paymentMethod: "cash",
        category: "loan",
        notes: "",
        date: new Date().toISOString().split("T")[0],
      });
      setShowAddForm(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to add transaction");
    }
  };

  const handleDelete = async (transactionId: string) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        await clientTransactionsAPI.deleteClientTransaction(transactionId);
        fetchData();
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to delete transaction");
      }
    }
  };

  if (loading && initialLoad) {
    return (
      <PageTransition>
        <div className="p-8 max-w-7xl mx-auto">
          {/* Breadcrumb skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-5 bg-gray-200 rounded-lg w-48"></div>
          </div>

          {/* Header skeleton */}
          <div className="mb-10 flex justify-between items-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded-lg w-56 mb-3"></div>
              <div className="h-5 bg-gray-200 rounded-lg w-72"></div>
            </div>
            <div className="h-12 bg-gray-200 rounded-xl w-40 animate-pulse"></div>
          </div>

          {/* Stats cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-white shadow-lg rounded-2xl border border-gray-100 p-6 animate-pulse"
              >
                <div className="h-5 bg-gray-200 rounded-lg w-20 mb-3"></div>
                <div className="h-7 bg-gray-200 rounded-lg w-24"></div>
              </div>
            ))}
          </div>

          <LoadingSkeleton variant="table" count={5} />
        </div>
      </PageTransition>
    );
  }

  if (!client) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Client not found
          </h3>
          <p className="text-gray-600 mb-6">
            The client you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/clients"
            className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-primary-500 hover:bg-primary-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageTransition className="min-h-screen bg-gray-50">
      <div className="p-8 max-w-7xl mx-auto">
        {/* Breadcrumb Navigation */}
        <nav className="mb-8">
          <div className="flex justify-start">
            <Link
              to="/clients"
              className="inline-flex items-center text-primary-600 hover:text-primary-800 transition-colors font-medium text-left"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="text-left">Back to Clients</span>
            </Link>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-10 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 text-left">
              {client.name}
            </h1>
            <p className="text-gray-600 text-lg">
              Transaction history and financial relationship
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportPDF}
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-primary-700 hover:bg-primary-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16v-8m0 8l-4-4m4 4l4-4M4 20h16"
                />
              </svg>
              Download as PDF
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-primary-500 hover:bg-primary-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Add New Transaction
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-8 bg-red-50 border-l-4 border-red-400 rounded-lg p-4 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError("")}
                  className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 transition-colors"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Transaction Form */}
        {showAddForm && client && (
          <AddTransactionForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => setShowAddForm(false)}
            clientName={client.name}
          />
        )}

        {/* Client Summary */}
        <ClientSummary client={client} />

        {/* Transactions List */}
        <TransactionsList
          transactions={transactions}
          clientName={client.name}
          setShowAddForm={setShowAddForm}
          handleDelete={handleDelete}
        />
      </div>
    </PageTransition>
  );
};

export default ClientTransactions;
